import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { Mail, MessageCircle, Instagram, Send, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "../components/Reveal";
import { PageHeader } from "./About";
import { useLang } from "../contexts/LanguageContext";
import { t } from "../i18n/translations";
import useSEO from "../hooks/useSEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const interestKeyMap = {
  sponsorship: 0,
  partnership: 0,
  media: 1,
};

const Contact = () => {
  const { lang } = useLang();
  useSEO({
    title: lang === "en"
      ? "Contact Revelo — Partner with Us"
      : "聯絡 Revelo — 與我們合作",
    description: lang === "en"
      ? "Get in touch with Revelo to host a workshop, partner with us or join the anti-scam movement in Hong Kong."
      : "與 Revelo 聯絡，預約工作坊、建立合作或加入香港反詐騙行動。",
    lang,
  });
  const tr = t[lang];
  const location = useLocation();

  const initialInterest = (() => {
    const params = new URLSearchParams(location.search);
    const raw = params.get("interest");
    if (!raw) return tr.contact.interests[0];
    if (Object.prototype.hasOwnProperty.call(interestKeyMap, raw)) {
      return tr.contact.interests[interestKeyMap[raw]];
    }
    return tr.contact.interests.includes(raw) ? raw : tr.contact.interests[0];
  })();

  const [form, setForm] = useState({
    name: "",
    organisation: "",
    email: "",
    phone: "",
    interest: initialInterest,
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Re-sync interest if interests labels change with language
    if (!tr.contact.interests.includes(form.interest)) {
      setForm((f) => ({ ...f, interest: tr.contact.interests[0] }));
    }
  }, [lang, tr.contact.interests, form.interest]);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        organisation: form.organisation,
        email: form.email,
        phone: form.phone,
        interest: form.interest,
        message: form.message,
        language: lang,
      });
      toast.success(tr.contact.fields.success);
      setForm({ name: "", organisation: "", email: "", phone: "", interest: tr.contact.interests[0], message: "" });
    } catch (err) {
      toast.error(tr.contact.fields.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHeader
        eyebrow={lang === "en" ? "Get in touch" : "聯絡我們"}
        title={tr.contact.title}
        intro={tr.contact.intro}
        heroImage="https://customer-assets.emergentagent.com/job_youth-care-hk/artifacts/x0edare1_image.png"
      />

      {/* Volunteer Recruitment — Google Form CTA (above contact form) */}
      <section className="pt-12 lg:pt-20">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
          <Reveal>
            <div
              data-testid="volunteer-cta"
              className="bg-[#0A192F] text-white p-10 lg:p-14 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8"
            >
              <div className="max-w-2xl">
                <p className="text-xs uppercase tracking-[0.28em] text-[#E9A229] font-bold">
                  {tr.volunteer.eyebrow}
                </p>
                <h3 className="mt-4 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight">
                  {tr.volunteer.title}
                </h3>
                <p className="mt-5 text-slate-300 leading-relaxed">
                  {tr.volunteer.body}
                </p>
              </div>
              <a
                href={tr.volunteer.formUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="volunteer-form-cta"
                className="btn-pill shrink-0 inline-flex items-center gap-2 bg-[#E9A229] hover:bg-[#C7841B] text-white px-8 py-4 rounded-full font-bold uppercase tracking-wide transition-all"
              >
                {tr.volunteer.cta}
                <ArrowRight size={18} className="btn-arrow" />
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-12 lg:py-20">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid lg:grid-cols-5 gap-8 lg:gap-12">
            {/* Contact info */}
            <div className="lg:col-span-2 space-y-5">
              <Reveal>
                <a href="mailto:revelo.hk@gmail.com" data-testid="contact-info-email" className="block bg-white border border-slate-100 rounded-none p-6 hover:shadow-md hover:-translate-y-0.5 transition-all">
                  <div className="flex items-center gap-3">
                    <div className="h-11 w-11 rounded-none bg-[#163B71]/5 text-[#163B71] flex items-center justify-center"><Mail size={20} /></div>
                    <div>
                      <p className="text-xs uppercase tracking-widest text-slate-500">Email</p>
                      <p className="font-semibold text-[#0A192F]">{tr.contact.email}</p>
                    </div>
                  </div>
                </a>
              </Reveal>
              <Reveal delay={0.05}>
                <a href="https://wa.me/85254210539" target="_blank" rel="noreferrer" data-testid="contact-info-whatsapp" className="block bg-white border border-slate-100 rounded-none p-6 hover:shadow-md hover:-translate-y-0.5 transition-all">
                  <div className="flex items-center gap-3">
                    <div className="h-11 w-11 rounded-none bg-[#E9A229]/10 text-[#E9A229] flex items-center justify-center"><MessageCircle size={20} /></div>
                    <div>
                      <p className="text-xs uppercase tracking-widest text-slate-500">WhatsApp</p>
                      <p className="font-semibold text-[#0A192F]">{tr.contact.whatsapp}</p>
                    </div>
                  </div>
                </a>
              </Reveal>
              <Reveal delay={0.1}>
                <a href="https://instagram.com/revelo.hk" target="_blank" rel="noreferrer" data-testid="contact-info-instagram" className="block bg-white border border-slate-100 rounded-none p-6 hover:shadow-md hover:-translate-y-0.5 transition-all">
                  <div className="flex items-center gap-3">
                    <div className="h-11 w-11 rounded-none bg-[#163B71]/5 text-[#163B71] flex items-center justify-center"><Instagram size={20} /></div>
                    <div>
                      <p className="text-xs uppercase tracking-widest text-slate-500">Instagram</p>
                      <p className="font-semibold text-[#0A192F]">{tr.contact.instagram}</p>
                    </div>
                  </div>
                </a>
              </Reveal>
            </div>

            {/* Form */}
            <Reveal className="lg:col-span-3">
              <form
                onSubmit={onSubmit}
                data-testid="contact-form"
                className="bg-white border border-slate-100 rounded-none p-8 lg:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
              >
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label={tr.contact.fields.name} required>
                    <input name="name" value={form.name} onChange={onChange} required data-testid="contact-input-name" className="w-full bg-[#F8FAFC] border border-slate-200 focus:border-[#163B71] focus:ring-2 focus:ring-[#163B71]/10 rounded-none px-4 py-3 outline-none transition" />
                  </Field>
                  <Field label={tr.contact.fields.organisation} required>
                    <input name="organisation" value={form.organisation} onChange={onChange} required data-testid="contact-input-organisation" className="w-full bg-[#F8FAFC] border border-slate-200 focus:border-[#163B71] focus:ring-2 focus:ring-[#163B71]/10 rounded-none px-4 py-3 outline-none transition" />
                  </Field>
                  <Field label={tr.contact.fields.email} required>
                    <input type="email" name="email" value={form.email} onChange={onChange} required data-testid="contact-input-email" className="w-full bg-[#F8FAFC] border border-slate-200 focus:border-[#163B71] focus:ring-2 focus:ring-[#163B71]/10 rounded-none px-4 py-3 outline-none transition" />
                  </Field>
                  <Field label={tr.contact.fields.phone} required>
                    <input name="phone" value={form.phone} onChange={onChange} required data-testid="contact-input-phone" className="w-full bg-[#F8FAFC] border border-slate-200 focus:border-[#163B71] focus:ring-2 focus:ring-[#163B71]/10 rounded-none px-4 py-3 outline-none transition" />
                  </Field>
                  <div className="sm:col-span-2">
                    <Field label={tr.contact.fields.interestLabel} required>
                      <select name="interest" value={form.interest} onChange={onChange} required data-testid="contact-input-interest" className="w-full bg-[#F8FAFC] border border-slate-200 focus:border-[#163B71] focus:ring-2 focus:ring-[#163B71]/10 rounded-none px-4 py-3 outline-none transition">
                        {tr.contact.interests.map((opt) => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </Field>
                  </div>
                  <div className="sm:col-span-2">
                    <Field label={tr.contact.fields.message} required>
                      <textarea name="message" value={form.message} onChange={onChange} required rows={5} data-testid="contact-input-message" className="w-full bg-[#F8FAFC] border border-slate-200 focus:border-[#163B71] focus:ring-2 focus:ring-[#163B71]/10 rounded-none px-4 py-3 outline-none transition resize-none" />
                    </Field>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit"
                  className="mt-7 inline-flex items-center gap-2 bg-[#E9A229] hover:bg-[#C7841B] disabled:opacity-60 disabled:cursor-not-allowed text-white px-8 py-4 rounded-full font-bold uppercase tracking-wide shadow-sm hover:shadow-md transition-all"
                >
                  {submitting ? tr.contact.fields.submitting : tr.contact.fields.submit}
                  {!submitting && <Send size={16} />}
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
};

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="block text-sm font-medium text-slate-700 mb-1.5">{label}{required && <span className="text-[#E9A229] ml-0.5">*</span>}</span>
    {children}
  </label>
);

export default Contact;
